/**
 * The boot screen's mark: the app icon's three blocks rise out of nothing and
 * sink back while the app loads. It is the boot screen's report that
 * something is happening (*Motion*), so it stands where the spinner stood: a
 * screen runs one loop. `boot-mark-svg.ts` says why it is an SVG that animates
 * itself with CSS, and how reduced motion holds it on the whole mark.
 *
 * **The page's clock.** It draws the string index.html already shows, then
 * starts its animations at the page's own start, as index.html's script does
 * for the copy before React. React's first draw, and each boot screen that
 * takes over from another (the database, the first page, the session), carry
 * the loop on instead of starting it again.
 */
import { useId, useLayoutEffect, useMemo, useRef, type ReactElement } from 'react';

import { bootMarkSvg } from './boot-mark-svg';

export function BootMark(): ReactElement {
  const boxRef = useRef<HTMLSpanElement>(null);
  // A mask id of its own: a page never shows two, but one boot screen hands
  // over to the next in a single commit.
  const id = `boot-mark-${useId().replace(/[^\w-]/g, '')}`;
  const markup = useMemo(() => ({ __html: bootMarkSvg({ id }) }), [id]);

  useLayoutEffect(() => {
    const svg = boxRef.current?.firstElementChild;
    if (svg === null || svg === undefined) return;
    for (const animation of svg.getAnimations({ subtree: true })) animation.startTime = 0;
  }, [markup]);

  return <span ref={boxRef} className="contents" dangerouslySetInnerHTML={markup} />;
}
